import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { Text } from "./Text";

/**
 * Centered empty / placeholder state — the shared shape for "nothing here yet",
 * 404, route errors, and stage-0 page stubs. Supports distinct loading and
 * error treatments while preserving the same semantic content order.
 */
export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  state?: "empty" | "no-results" | "loading" | "error";
  tone?: "neutral" | "image" | "video" | "voice" | "text" | "asset";
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  state = "empty",
  tone = "neutral",
}: EmptyStateProps) {
  if (state === "loading") {
    return (
      <div
        role="status"
        aria-atomic="true"
        className={cn(
          "flex min-h-44 flex-col gap-3 rounded-lg bg-paper p-5",
          className,
        )}
      >
        <span className="sr-only">{title}</span>
        <span
          aria-hidden="true"
          className="h-8 w-8 rounded-sm bg-canvas-soft"
        />
        <span
          aria-hidden="true"
          className="h-3 w-2/3 rounded-[4px] bg-canvas-soft"
        />
        <span
          aria-hidden="true"
          className="h-3 w-1/2 rounded-[4px] bg-line-soft"
        />
        <span
          aria-hidden="true"
          className="mt-1 min-h-16 flex-1 rounded-sm bg-canvas-soft"
        />
      </div>
    );
  }

  return (
    <div
      role={state === "error" ? "alert" : undefined}
      className={cn(
        "flex min-h-44 flex-col items-center justify-center gap-3 rounded-lg px-6 py-8 text-center",
        state === "error" ? "bg-danger-subtle" : emptyTone[tone],
        className,
      )}
    >
      {icon ? (
        <div
          aria-hidden="true"
          className={cn(
            "grid size-[52px] place-items-center rounded-md [&_svg]:size-6",
            state === "error"
              ? "bg-danger text-white"
              : tone === "neutral"
                ? "bg-canvas-soft text-ink-soft"
                : "bg-white/20 text-white",
          )}
        >
          {icon}
        </div>
      ) : null}
      <div className="flex max-w-sm flex-col gap-2">
        <Text
          variant="ui"
          as="h2"
          className={cn(
            state === "error" && "text-danger-text",
            state !== "error" && tone !== "neutral" && "text-white",
          )}
        >
          {title}
        </Text>
        {description ? (
          <Text
            variant="body"
            as="p"
            className={cn(
              "max-w-[300px] text-[13px] leading-[1.6]",
              state === "error" && "text-danger-text",
              state !== "error" && tone !== "neutral" && "text-white/80",
            )}
          >
            {description}
          </Text>
        ) : null}
      </div>
      {action ? <div className="mt-0.5">{action}</div> : null}
    </div>
  );
}

const emptyTone: Record<NonNullable<EmptyStateProps["tone"]>, string> = {
  neutral: "bg-paper",
  image: "bg-media-image text-white",
  video: "bg-media-video text-white",
  voice: "bg-media-voice text-white",
  text: "bg-media-text text-white",
  asset: "bg-media-asset text-white",
};
